import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { createQuoteScheduler } from "@/modules/dashboard/crypto-swaps/utils/cryptoSwapQuoteScheduler";
import {
  type NativeQuote,
  type NativeQuoteRequest,
  PaymentsQuotesService,
} from "@/modules/dashboard/dispersions/services/paymentsQuotes.service";
import { useQuoteExpiry } from "@/shared/hooks/useQuoteExpiry";
import { normalizePaymentError } from "@/shared/services/payments/paymentErrors";

import { quoteMatchesSwap, type SwapAnchor, swapQuoteRequest } from "../utils/walletSwap";

const QUOTE_DEBOUNCE_MS = 500;

interface QuoteJob {
  request: NativeQuoteRequest;
  signature: string;
}

interface SettledQuote {
  signature: string;
  quote: NativeQuote | null;
  error: string | null;
  /** The pair could not be priced right now (a rate provider down): a 503, not the amount's fault. */
  unavailable: boolean;
  retryable: boolean;
}

export interface UseWalletSwapQuoteParams {
  originCurrency: string;
  destinationCurrency: string;
  /** Decimal string in the anchor's currency, or null while nothing usable is typed. */
  amount: string | null;
  anchor: SwapAnchor;
  enabled: boolean;
}

export interface WalletSwapQuoteState {
  /** Prices exactly what is on screen right now; null while re-pricing. */
  quote: NativeQuote | null;
  /** The last answer for this pair, for a dimmed figure while the next one arrives. */
  lastQuote: NativeQuote | null;
  loading: boolean;
  error: string | null;
  unavailable: boolean;
  retryable: boolean;
  refresh: () => void;
}

/**
 * Prices a conversion between two of the organization's wallets: debounced on typing, immediate when only the
 * wallets or the anchored side moved, last answer wins, and re-priced silently on expiry. A quote counts only
 * while it prices exactly the pair and the amount on screen (`quoteMatchesSwap` + the request signature), so
 * a slow answer to an older request can never be confirmed.
 */
export function useWalletSwapQuote({
  originCurrency,
  destinationCurrency,
  amount,
  anchor,
  enabled,
}: UseWalletSwapQuoteParams): WalletSwapQuoteState {
  const [tick, setTick] = useState(0);
  const [settled, setSettled] = useState<SettledQuote | null>(null);

  const signature =
    enabled && amount ? `${originCurrency}>${destinationCurrency}|${anchor}|${amount}|${tick}` : null;
  const request = useMemo(
    () =>
      signature && amount ? swapQuoteRequest({ originCurrency, destinationCurrency, amount, anchor }) : null,
    [signature, amount, originCurrency, destinationCurrency, anchor],
  );

  const scheduler = useMemo(
    () =>
      createQuoteScheduler<QuoteJob, NativeQuote>({
        delayMs: QUOTE_DEBOUNCE_MS,
        run: async ({ request: body }) => (await PaymentsQuotesService.createQuote(body)).data.quote,
        onResult: (quote, job) =>
          setSettled({ signature: job.signature, quote, error: null, unavailable: false, retryable: false }),
        onError: (error, job) => {
          const normalized = normalizePaymentError(error);
          const status = normalized.status ?? 0;
          setSettled({
            signature: job.signature,
            quote: null,
            error: normalized.message || null,
            unavailable: status === 503,
            retryable: normalized.kind === "network" || normalized.kind === "unknown" || status >= 500,
          });
        },
      }),
    [],
  );

  const pricedAmount = useRef<string | null>(null);
  useEffect(() => {
    if (!signature || !request) {
      scheduler.cancel();
      pricedAmount.current = null;
      return;
    }
    const immediate = pricedAmount.current === amount;
    pricedAmount.current = amount;
    scheduler.schedule({ request, signature }, immediate);
  }, [signature, request, amount, scheduler]);

  useEffect(() => () => scheduler.cancel(), [scheduler]);

  const current = signature && settled?.signature === signature ? settled : null;
  const quote =
    current?.quote && quoteMatchesSwap(current.quote, originCurrency, destinationCurrency)
      ? current.quote
      : null;
  const lastQuote =
    settled?.quote && quoteMatchesSwap(settled.quote, originCurrency, destinationCurrency)
      ? settled.quote
      : null;

  const refresh = useCallback(() => setTick((value) => value + 1), []);
  useQuoteExpiry(quote?.expires_at ?? null, refresh);

  return {
    quote,
    lastQuote,
    loading: !!signature && !current,
    error: current?.error ?? null,
    unavailable: current?.unavailable ?? false,
    retryable: current?.retryable ?? false,
    refresh,
  };
}
